import React, { memo, type ReactElement, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  Link as DSLink,
  Menu,
  MenuItem,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_BLD } from '../../../constants';

import './ListDateFilter.css';

interface ListDateFilterState {
  options: MenuOption[];
  currentStartDate?: Date | null;
  currentEndDate?: Date | null;
  trigger: React.ReactElement;
  maxDate: Date;
  handleOptionsClick: (
    currentStartDate?: Date | null,
    currentEndDate?: Date | null
  ) => void | false | undefined;
  isStartDateDisabled: boolean;
}

/**
 * A date filter menu component that provides preset date range options
 * and custom start/end date pickers. Supports toggling between preset
 * options and manual date selection, with a clear action to reset.
 * Prevents selecting identical start and end date-times and displays
 * an inline error when the user picks matching values.
 *
 * @param props - {@link ListDateFilterState}
 * @param props.options - Preset date range menu options
 * @param props.trigger - The element that triggers the menu to open
 * @param props.maxDate - Maximum selectable date
 * @param props.currentStartDate - Currently applied start date (defaults to `null`)
 * @param props.currentEndDate - Currently applied end date (defaults to `null`)
 * @param props.handleOptionsClick - Callback invoked when a date range is selected or cleared
 * @param props.isStartDateDisabled - Whether the start date picker and preset options are disabled
 * @returns The rendered date filter menu
 */
function ListDateFilter({
  options,
  trigger,
  maxDate,
  currentStartDate,
  currentEndDate,
  handleOptionsClick,
  isStartDateDisabled,
}: ListDateFilterState): ReactElement {
  const intl = useIntl();

  const [optionsMap, setOptionsMap] = useState(options);
  const [startDate, setStartDate] = useState<Date | undefined | null>(null);
  const [endDate, setEndDate] = useState<Date | undefined | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sameDateError, setSameDateError] = useState(false);

  const now = new Date();

  const hours = now.getHours(); // 0–23 (already 24-hour format)
  const minutes = now.getMinutes(); // 0–59

  const isSameDateTime = (
    a: Date | null | undefined,
    b: Date | null | undefined
  ): boolean => {
    if (!a || !b) {
      return false;
    }

    return a.getTime() === b.getTime();
  };

  // Manipulate options based on applied options
  useEffect(() => {
    const isOptionSelected = Boolean(optionsMap.some((o) => o.isChecked));

    if (currentStartDate && !isOptionSelected) {
      setStartDate(currentStartDate);
    } else {
      setStartDate(null);
    }

    if (currentEndDate && !isOptionSelected) {
      setEndDate(currentEndDate);
    } else {
      setEndDate(null);
    }
  }, [currentStartDate, currentEndDate, optionsMap]);

  return (
    <Menu
      isPortalEnabled
      isMenuExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      isOutsideClickEnabled={false}
      CustomMenu={
        <>
          <ul
            role='listbox'
            tabIndex={-1}
            className='MenuMulti-items'
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}>
            {optionsMap.map((option) => (
              <MenuItem
                key={option.value}
                option={{
                  label: option.label,
                  value: option.value,
                  isChecked: option.isChecked,
                  isDisabled: isStartDateDisabled,
                }}
                onClick={() => {
                  setIsExpanded(false);

                  const [startDays, endDays] = JSON.parse(
                    option.value
                  ) as number[];
                  const newStartDate = new Date(
                    Date.now() + startDays * 24 * 60 * 60 * 1000
                  );
                  const newEndDate = new Date(
                    Date.now() + endDays * 24 * 60 * 60 * 1000
                  );

                  setOptionsMap((prev) =>
                    prev.map((prevOption) => ({
                      ...prevOption,
                      isChecked: prevOption.value === option.value,
                    }))
                  );

                  handleOptionsClick(newStartDate, newEndDate);
                }}
                tabIndex={0}
              />
            ))}
            <Box tag='div' className='ListDateFilter-datePicker'>
              <DateAndTimePicker
                kind='dateAndTimeStart'
                label='Start Date'
                dateFormat='MM/dd/yyyy, h:mm aa'
                isDisabled={isStartDateDisabled}
                startDate={startDate}
                endDate={endDate}
                setStartDate={(date: Date | null) => {
                  if (isSameDateTime(date, endDate)) {
                    setSameDateError(true);
                    setStartDate(null);

                    return;
                  }

                  setSameDateError(false);

                  const isOptionSelected = Boolean(
                    optionsMap.some((o) => o.isChecked)
                  );

                  setOptionsMap((prev) =>
                    prev.map((prevOption) => ({
                      ...prevOption,
                      isChecked: false,
                    }))
                  );

                  handleOptionsClick(
                    date,
                    isOptionSelected ? null : currentEndDate
                  );

                  const isZeroTime =
                    date &&
                    date.getHours() === 0 &&
                    date.getMinutes() === 0 &&
                    date.getSeconds() === 0;

                  if (date !== null && !isZeroTime && endDate) {
                    setIsExpanded(false);
                  }
                }}
                setEndDate={setEndDate}
                maxDate={maxDate}
                minDate={now}
                timeMin={{ minutes, hours }}
                portalId='root'
              />
              <Typography kind='body3' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='O7CWxp'
                  defaultMessage='to'
                  description='Separator text between start date and end date in ListDateFilter component'
                />
              </Typography>
              <DateAndTimePicker
                kind='dateAndTimeEnd'
                label='Last Date'
                dateFormat='MM/dd/yyyy, h:mm aa'
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={(date: Date | null) => {
                  if (isSameDateTime(startDate, date)) {
                    setSameDateError(true);
                    setEndDate(null);

                    return;
                  }

                  setSameDateError(false);

                  const isOptionSelected = Boolean(
                    optionsMap.some((o) => o.isChecked)
                  );

                  setOptionsMap((prev) =>
                    prev.map((prevOption) => ({
                      ...prevOption,
                      isChecked: false,
                    }))
                  );

                  handleOptionsClick(
                    isOptionSelected ? null : currentStartDate,
                    date
                  );

                  const isZeroTime =
                    date &&
                    date.getHours() === 0 &&
                    date.getMinutes() === 0 &&
                    date.getSeconds() === 0;

                  if (startDate && !isZeroTime && date !== null) {
                    setIsExpanded(false);
                  }
                }}
                maxDate={maxDate}
                minDate={new Date()}
                portalId='root'
              />
              {sameDateError && (
                <div className='ListDateFilter-error'>
                  <InlineMessage
                    text={intl.formatMessage({
                      id: 'sGaFwd',
                      defaultMessage: 'Start and end date cannot be the same',
                      description:
                        'Error message when user selects the same start and end date time',
                    })}
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                </div>
              )}
            </Box>
          </ul>
          <div
            role='listbox'
            tabIndex={-1}
            className='ListDateFilter-footer'
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}>
            <span />
            <DSLink
              text='Clear'
              size='small'
              kind='button'
              onClick={() => {
                if (isStartDateDisabled) {
                  handleOptionsClick(startDate, null);

                  return;
                }

                setOptionsMap((prev) =>
                  prev.map((prevOption) => ({
                    ...prevOption,
                    isChecked: false,
                  }))
                );

                handleOptionsClick(null, null);
              }}
              shouldStopPropagation
            />
          </div>
        </>
      }
      trigger={trigger}
    />
  );
}

ListDateFilter.defaultProps = {
  currentStartDate: null,
  currentEndDate: null,
};

export default memo(ListDateFilter);
